import { ErrorBoundary } from "@prismicio/editor-ui"
import { type RefObject, Suspense } from "react"

import { type EditorConfig, useEditorConfig } from "../../EditorConfig"
import type { MediaAssetType } from "../MediaLibrary/hooks/mediaLibraryData"
import { SelectionManagerProvider } from "../SelectionManager"
import { EmptyMediaList } from "./components/EmptyMediaList"
import { MediaLibraryActions } from "./components/MediaLibraryActions"
import {
  MediaLibraryProvider,
  useMediaLibrary
} from "./components/MediaLibraryContext"
import { MediaLibraryDropzone } from "./components/MediaLibraryDropzone"
import { MediaLibraryRenderError } from "./components/MediaLibraryRenderError"
import { MediaLibrarySkeleton } from "./components/MediaLibrarySkeleton"
import { MediaLibraryToastProvider } from "./components/MediaLibraryToast"
import { MediaLibraryToolbar } from "./components/MediaLibraryToolbar"
import { MediaList } from "./components/MediaList"
import { Sidepane } from "./components/Sidepane"
import { MediaLibraryLayout } from "./MediaLibraryLayout"

interface MediaLibraryProps {
  /**
   * The type of media to fetch from the media library.
   * @default all
   */
  assetType?: MediaAssetType
  /**
   *  A ref to render the actions container outside the media library.
   */
  actionContainerRef?: RefObject<HTMLDivElement>
  onSelectedChange?: (ids: Set<string>) => void
}

export function MediaLibrary(props: MediaLibraryProps) {
  const { assetType, onSelectedChange, actionContainerRef } = props

  // TODO: Disable Select all in every env that is not integration,
  // to be fixed by [PBD-1029]
  const config = useEditorConfig()
  const isSelectAllEnabled = isIntegrationEnv(config)

  return (
    <ErrorBoundary renderError={() => <MediaLibraryRenderError />}>
      <Suspense
        fallback={
          <MediaLibrarySkeleton actionContainerRef={actionContainerRef} />
        }
      >
        <SelectionManagerProvider isSelectAllEnabled={isSelectAllEnabled}>
          <MediaLibraryProvider
            assetType={assetType}
            actionContainerRef={actionContainerRef}
            onSelectedChange={onSelectedChange}
          >
            <MediaLibraryDropzone>
              <MediaLibraryContent />
            </MediaLibraryDropzone>
          </MediaLibraryProvider>
        </SelectionManagerProvider>
      </Suspense>
    </ErrorBoundary>
  )
}

function MediaLibraryContent() {
  const { media } = useMediaLibrary()

  return (
    <MediaLibraryToastProvider>
      <MediaLibraryLayout
        sidePane={<Sidepane />}
        mediaActions={<MediaLibraryActions />}
        mediaToolbar={<MediaLibraryToolbar />}
        mediaList={media.length === 0 ? <EmptyMediaList /> : <MediaList />}
      />
    </MediaLibraryToastProvider>
  )
}

// HACK: this temp hack will be removed by [PBD-1029] - with this we avoid to
// propagate the PRISMIC_DOMAIN env all the way from wroom
function isIntegrationEnv(config: EditorConfig): boolean {
  if ("assetsApiBaseUrl" in config) {
    return config.assetsApiBaseUrl.host.includes("wroom-beta.com")
  }
  return false
}
